import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { onAuthChange, waitForSignupGate, signInAsGuest, logOut } from './firebase/auth';
import { auth, functions } from './firebase/config';
import { httpsCallable } from 'firebase/functions';
import { getChats, deleteChat } from './firebase/db';
import { setMonitoringUser, reportError } from './monitoring';
import AppShell, { UnsavedModal } from './components/AppShell';
import FullArchiveChat from './components/FullArchiveChat';
import AuthForm from './components/AuthForm';
import NameOnboarding from './components/NameOnboarding';
import Toast from './components/Toast';
import ConfirmModal from './components/ConfirmModal';
import Icon from './components/Icon';
import { AprMark } from './components/BrandMarks';
import { TeamRoleContext } from './components/TeamRoleContext';
import { readArrival, bankQuestion, isCampaignSession, logArrivalEvent } from './utils/campaign';
import { prefetchApprovedAnswer } from './utils/canonical';
import { useLightReading } from './utils/theme';
import { silenceVoiceRepliesForCampaign } from './utils/voicePreference';
import { setTeamRole, localTestRole } from './utils/teamVoice';
import { trackView } from './utils/analytics';
import { isDeliberateLeave } from './utils/giving';

// The link this tab opened with, read once before anything renders, then
// cleaned out of the address bar (utils/campaign.js, BRAND_SPEC §15.1).
const ARRIVAL = readArrival();
// Voice replies are off on campaign landings, whatever was saved (§11.1).
if (isCampaignSession()) silenceVoiceRepliesForCampaign();
if (ARRIVAL.fresh && (ARRIVAL.landing || Object.keys(ARRIVAL.utm).length)) {
  // Content-free: a bank ID and campaign fields, never words (§15.3).
  logArrivalEvent('campaign_landing', { ...(ARRIVAL.landing ? { q: ARRIVAL.landing } : {}), ...ARRIVAL.utm });
}
// A landing with a question: Rob's approved first answer, when there is one
// the bundle doesn't carry yet, is fetched now, beside the sign-in, so it is
// there by the time the question has typed itself in (utils/canonical.js).
if (ARRIVAL.fresh && ARRIVAL.landing) prefetchApprovedAnswer(ARRIVAL.landing);
// TODO(TEAM_APP_SPEC §8.1, Lane B): ARRIVAL.next ('meet' | 'talk' | 'seer',
// from ?next=) opens the handoff panel on that choice, and ARRIVAL.campaign
// rides along with the request. Both are kept in sessionStorage
// ('apr-arrival-v1') until then.

// Secondary views load on demand so the initial bundle only carries the
// shell + the default archive chat.
const SermonTimeline = lazy(() => import(/* webpackChunkName: "sermon-timeline" */ './components/SermonTimeline'));
const MultiSermonChat = lazy(() => import(/* webpackChunkName: "multi-sermon-chat" */ './components/MultiSermonChat'));
const ChatInterface = lazy(() => import(/* webpackChunkName: "chat-interface" */ './components/ChatInterface'));
const AccountMenu = lazy(() => import(/* webpackChunkName: "account-menu" */ './components/AccountMenu'));
const ContactPage = lazy(() => import(/* webpackChunkName: "contact-page" */ './components/ContactPage'));

const viewFallback = (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: '200px',
      color: 'var(--brand-muted)',
      fontSize: '0.8125rem',
      fontWeight: 500,
    }}
  >
    Loading&hellip;
  </div>
);

const SKIP_WARNING_KEY = 'apr_skip_unsaved_warning';
const FOCUSED_KEY = 'apr-focused-selection';

/** askpastorrob.com/contact opens the Contact us view (hosting serves index.html for it). */
const CONTACT_PATH = '/contact';

function onContactPath() {
  try {
    return window.location.pathname.replace(/\/+$/, '') === CONTACT_PATH;
  } catch (e) {
    return false;
  }
}

function skipUnsavedWarning() {
  try {
    return localStorage.getItem(SKIP_WARNING_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

/** Which Focused Study selection was open, so a reload lands back in it. */
function readFocused() {
  try {
    const raw = sessionStorage.getItem(FOCUSED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.videoIds) || !parsed.videoIds.length) return null;
    return { videoIds: parsed.videoIds, chatId: parsed.chatId || null, key: parsed.key || parsed.videoIds.join(',') };
  } catch (e) {
    return null;
  }
}

function writeFocused(focused) {
  try {
    if (focused) sessionStorage.setItem(FOCUSED_KEY, JSON.stringify(focused));
    else sessionStorage.removeItem(FOCUSED_KEY);
  } catch (e) { /* ignore */ }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Views: 'chat' (archive) | 'focused' (picker) | 'multi' | 'single' | 'contact'
  const [currentView, setCurrentView] = useState(() => (onContactPath() ? 'contact' : 'chat'));
  // The one Focused Study conversation. It stays mounted (hidden) while the
  // visitor looks elsewhere, so an answer in flight is never lost.
  const [focused, setFocused] = useState(readFocused);
  const [activeChatId, setActiveChatId] = useState(null);
  const [archiveChatId, setArchiveChatId] = useState(null);
  const [resetNonce, setResetNonce] = useState(0);
  const [toast, setToast] = useState(null); // { message, type }
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [myRole, setMyRole] = useState(null);
  const [chats, setChats] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // chat awaiting delete confirmation
  const [deleting, setDeleting] = useState(false);
  // Answers that finished while the visitor was on another view
  const [unseen, setUnseen] = useState({ archive: false, focused: false });
  const guestSignInFailed = useRef(false);
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;
  const { lightReading, toggleLightReading } = useLightReading();
  // The ad's question is asked once, in the first conversation of this tab.
  const [landing] = useState(() => (ARRIVAL.fresh && ARRIVAL.landing ? bankQuestion(ARRIVAL.landing) : null));
  const [prefill] = useState(() => (ARRIVAL.fresh ? ARRIVAL.prefill : null));
  // A paid ad sent this tab (utils/campaign.js isAdArrival, the server's own
  // rule): the front page holds nothing back for them, fresh or reloaded.
  const [fromAd] = useState(() => Boolean(ARRIVAL.ad));

  // SermonTimeline is deliberately kept mounted (behind display:none) once
  // visited so selections survive leaving the view. Because it is lazy, we
  // also avoid rendering it before the first visit so its chunk isn't
  // fetched until Focused Study is actually opened.
  const [focusedVisited, setFocusedVisited] = useState(false);
  if (currentView === 'focused' && !focusedVisited) {
    setFocusedVisited(true); // render-phase state adjustment (React-sanctioned)
  }

  // Unsaved-conversation guard state
  const dirtyMapRef = useRef({ archive: false, focused: false });
  const [pendingNav, setPendingNav] = useState(null); // { action } while modal is open
  const [skipChecked, setSkipChecked] = useState(false);

  // Contact us keeps its half-written message once opened, like the sermon picker.
  const [contactVisited, setContactVisited] = useState(() => onContactPath());
  if (currentView === 'contact' && !contactVisited) {
    setContactVisited(true);
  }

  const focusedView = focused ? (focused.videoIds.length > 1 ? 'multi' : 'single') : null;
  const showingFocusedChat = focusedView !== null && (currentView === 'multi' || currentView === 'single');

  useEffect(() => { writeFocused(focused); }, [focused]);

  // The address follows the view: /contact while Contact us is open, / after.
  // The back button leaves Contact us the way it came.
  useEffect(() => {
    try {
      if (currentView === 'contact' && !onContactPath()) window.history.pushState({ apr: 'contact' }, '', CONTACT_PATH);
      else if (currentView !== 'contact' && onContactPath()) window.history.replaceState(window.history.state, '', '/');
    } catch (e) { /* the address bar is decoration */ }
  }, [currentView]);

  useEffect(() => {
    const onPop = () => {
      if (onContactPath()) setCurrentView('contact');
      else setCurrentView((view) => (view === 'contact' ? 'chat' : view));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Which screen someone is on, as a count (utils/analytics.js); no words.
  useEffect(() => { trackView(currentView); }, [currentView]);

  // Seeing a surface clears its "new answer" dot
  useEffect(() => {
    if (currentView === 'chat') setUnseen((u) => (u.archive ? { ...u, archive: false } : u));
    if (currentView === 'multi' || currentView === 'single') setUnseen((u) => (u.focused ? { ...u, focused: false } : u));
  }, [currentView]);

  // ------------------------------------------------------------------ auth

  useEffect(() => {
    const unsubscribe = onAuthChange(async (authUser) => {
      if (authUser) {
        await waitForSignupGate();
        // Copy into a fresh object: Firebase mutates the same User instance
        // in place, and an identical reference makes React skip the re-render.
        setUser({ ...auth.currentUser });
        setMonitoringUser(auth.currentUser);
        setShowAuthForm(false);
        setLoading(false);
      } else {
        // No session: enter as a guest automatically (no wall). Guests get a
        // UID for rate limiting only; their conversations can't be saved
        // to a list (the team still keeps them, privately: /privacy).
        if (!guestSignInFailed.current) {
          try {
            await signInAsGuest();
            return; // onAuthChange fires again with the guest user
          } catch (error) {
            reportError(error, { where: 'auth.guestSignIn' });
            guestSignInFailed.current = true;
          }
        }
        setUser(null);
        setMonitoringUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && localStorage.getItem('accountJustCreated') === 'true') {
      localStorage.removeItem('accountJustCreated');
      setTimeout(() => setToast({
        message: user.displayName ? `Welcome, ${user.displayName}. Your account is ready.` : 'Welcome. Your account is ready.',
        type: 'success',
      }), 100);
    }
  }, [user]);

  // Role probe (server enforces; this only decides whether to show the portal link, and the
  // team's pick of Rob's voice: utils/teamVoice.js)
  useEffect(() => {
    let cancelled = false;
    setMyRole(null);
    setTeamRole(null);
    const testRole = localTestRole(); // the phone harness only, on 127.0.0.1
    if (testRole !== undefined) {
      setMyRole(testRole);
      setTeamRole(testRole);
      return undefined;
    }
    if (user && !user.isAnonymous) {
      const probe = httpsCallable(functions, 'getMyRole');
      probe({})
        .then((result) => {
          if (cancelled) return;
          setMyRole(result.data?.role || null);
          setTeamRole(result.data?.role || null);
        })
        .catch(() => { if (!cancelled) setMyRole(null); });
    }
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Saved conversations for the sidebar
  const loadChats = useCallback(async () => {
    if (!user || user.isAnonymous) {
      setChats([]);
      return;
    }
    try {
      setChats(await getChats(user.uid));
    } catch (err) {
      reportError(err, { where: 'chats.load' });
    }
  }, [user?.uid, user?.isAnonymous]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // -------------------------------------------------- unsaved-loss guarding

  const setDirty = useCallback((surface, value) => {
    dirtyMapRef.current = { ...dirtyMapRef.current, [surface]: value };
  }, []);

  const anyDirty = () => Object.values(dirtyMapRef.current).some(Boolean);

  // Browser/tab close with an unsaved conversation anywhere
  useEffect(() => {
    const handler = (e) => {
      if (anyDirty() && !skipUnsavedWarning() && !isDeliberateLeave()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  /**
   * Runs `action` immediately unless one of the listed chat surfaces holds an
   * unsaved conversation that the action would discard — then asks first.
   */
  const guardNavigate = (surfaces, action) => {
    const wouldLose = surfaces.some((s) => dirtyMapRef.current[s]);
    if (wouldLose && !skipUnsavedWarning()) {
      setSkipChecked(false);
      setPendingNav({ action, surfaces });
      return;
    }
    action();
  };

  const confirmDiscard = () => {
    if (skipChecked) {
      try { localStorage.setItem(SKIP_WARNING_KEY, 'true'); } catch (e) { /* ignore */ }
    }
    const nav = pendingNav;
    setPendingNav(null);
    if (nav) {
      nav.surfaces.forEach((s) => setDirty(s, false));
      nav.action();
    }
  };

  // ------------------------------------------------------------ navigation

  const startNewChat = () => {
    setCurrentView('chat');
    setActiveChatId(null);
    setArchiveChatId(null);
    setResetNonce((n) => n + 1);
    setSidebarOpen(false);
  };

  const handleNewChat = () => guardNavigate(['archive'], startNewChat);

  const openFocused = (videoIds, chatId) => {
    setFocused({ videoIds, chatId: chatId || null, key: `${chatId || 'new'}:${videoIds.join(',')}:${Date.now()}` });
    setActiveChatId(chatId || null);
    setCurrentView(videoIds.length > 1 ? 'multi' : 'single');
    setSidebarOpen(false);
  };

  const handleSelectChat = (chat) => {
    const isArchive = chat.chatType === 'collections' || !chat.videoIds?.length;
    const open = () => {
      if (isArchive) {
        setActiveChatId(chat.id);
        setArchiveChatId(chat.id);
        setCurrentView('chat');
        setSidebarOpen(false);
      } else {
        openFocused(chat.videoIds, chat.id);
      }
    };
    // Opening a chat replaces whichever surface it lands on. A saved focused
    // chat that is already open just needs the view switched.
    if (!isArchive && focused && focused.chatId === chat.id) {
      setActiveChatId(chat.id);
      setCurrentView(focusedView);
      setSidebarOpen(false);
      return;
    }
    guardNavigate([isArchive ? 'archive' : 'focused'], open);
  };

  const openContact = () => {
    setSidebarOpen(false);
    setCurrentView('contact');
  };

  const handleDeleteChat = (chat) => setConfirmDelete(chat);

  const performDelete = async () => {
    const chat = confirmDelete;
    if (!chat) return;
    setDeleting(true);
    try {
      await deleteChat(user.uid, chat.id);
      setChats((prev) => prev.filter((c) => c.id !== chat.id));
      if (chat.id === archiveChatId) startNewChat();
      if (focused && chat.id === focused.chatId) {
        setFocused(null);
        setDirty('focused', false);
        if (currentView === 'multi' || currentView === 'single') setCurrentView('focused');
      }
      if (chat.id === activeChatId) setActiveChatId(null);
      setToast({ message: 'Conversation deleted.', type: 'info' });
    } catch (err) {
      reportError(err, { where: 'chats.delete' });
      setToast({ message: "Couldn't delete that conversation. Please try again.", type: 'error' });
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const handleMultiSermonStart = (videoIds) => {
    // Same selection as the open study → just go back to it
    if (focused && !focused.chatId && focused.videoIds.join(',') === videoIds.join(',')) {
      setCurrentView(videoIds.length > 1 ? 'multi' : 'single');
      return;
    }
    guardNavigate(['focused'], () => openFocused(videoIds, null));
  };

  const handleChatSaved = (chatId) => {
    setActiveChatId(chatId);
    loadChats();
  };

  const handleAnswerComplete = useCallback((surface) => {
    const view = currentViewRef.current;
    const visible = surface === 'archive' ? view === 'chat' : (view === 'multi' || view === 'single');
    if (!visible) setUnseen((u) => ({ ...u, [surface]: true }));
  }, []);

  const handleSignOut = () => {
    setShowAccount(false);
    guardNavigate(['archive', 'focused'], async () => {
      try {
        await logOut(); // onAuthChange drops us into a fresh guest session
        setFocused(null);
        startNewChat();
      } catch (err) {
        reportError(err, { where: 'auth.signOut' });
      }
    });
  };

  const handleRequestSignIn = () => {
    setSidebarOpen(false);
    setShowAuthForm(true);
  };

  const handleAccountDeleted = () => {
    setShowAccount(false);
    dirtyMapRef.current = { archive: false, focused: false };
    setFocused(null);
    startNewChat();
    setToast({ message: 'Your account and saved conversations were deleted.', type: 'info' });
  };

  // ---------------------------------------------------------------- render

  if (loading) {
    return (
      <div className="loading-container">
        <AprMark height={40} />
        <p className="loading-text">Opening Ask Pastor Rob…</p>
      </div>
    );
  }

  // Hard fallback only when guest entry itself is unavailable
  if (!user) {
    return <AuthForm />;
  }

  const needsName = !user.isAnonymous && !user.displayName?.trim();

  return (
    <TeamRoleContext.Provider value={myRole}>
      {needsName && (
        <NameOnboarding onComplete={(updatedUser) => setUser({ ...updatedUser })} />
      )}

      <AppShell
        user={user}
        myRole={myRole}
        chats={chats}
        activeChatId={activeChatId}
        currentView={currentView}
        unseen={unseen}
        hasFocused={Boolean(focused)}
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={setSidebarOpen}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        onOpenChat={() => { setSidebarOpen(false); setCurrentView('chat'); }}
        onFocusedStudy={() => { setSidebarOpen(false); setCurrentView(focused && unseen.focused ? focusedView : 'focused'); }}
        onResumeFocused={focused ? () => { setSidebarOpen(false); setCurrentView(focusedView); } : null}
        onAccount={() => { setSidebarOpen(false); setShowAccount(true); }}
        onContact={openContact}
        onRequestSignIn={handleRequestSignIn}
        onSignOut={handleSignOut}
        lightReading={lightReading}
        onToggleLightReading={toggleLightReading}
      >
        {/* The main chat — always mounted so a conversation in progress is
            never lost by looking around the app */}
        <div style={{ display: currentView === 'chat' ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <FullArchiveChat
            user={user}
            chatId={archiveChatId}
            resetNonce={resetNonce}
            landing={landing}
            prefill={prefill}
            fromAd={fromAd}
            onRequestSignIn={handleRequestSignIn}
            onDirtyChange={(d) => setDirty('archive', d)}
            onChatSaved={handleChatSaved}
            onAnswerComplete={() => handleAnswerComplete('archive')}
          />
        </div>

        {/* Focused Study picker — kept mounted to preserve selections.
            `active` matters: the scatter canvas can only size itself while
            visible, and `onBack` must ONLY switch the view (never touch
            chat ids / resetNonce) so the conversation behind it survives. */}
        <div style={{ display: currentView === 'focused' ? 'block' : 'none' }}>
          {focusedVisited && (
            <Suspense fallback={viewFallback}>
              <SermonTimeline
                user={user}
                onStartChat={handleMultiSermonStart}
                active={currentView === 'focused'}
                onBack={() => setCurrentView('chat')}
                resumeLabel={focused ? `Back to your ${focused.videoIds.length === 1 ? 'sermon' : `${focused.videoIds.length}-sermon`} study` : null}
                onResume={focused ? () => setCurrentView(focusedView) : null}
              />
            </Suspense>
          )}
        </div>

        {/* Contact us — mounted once opened, hidden when the visitor is elsewhere */}
        {contactVisited && (
          <div style={{ display: currentView === 'contact' ? 'block' : 'none' }}>
            <Suspense fallback={viewFallback}>
              <ContactPage user={user} active={currentView === 'contact'} onBack={() => setCurrentView('chat')} />
            </Suspense>
          </div>
        )}

        {/* The Focused Study conversation — mounted while it exists, hidden
            when the visitor is elsewhere, remounted (key) for a new selection */}
        {focused && (
          <div style={{ display: showingFocusedChat ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Suspense fallback={viewFallback}>
              {focused.videoIds.length > 1 ? (
                <MultiSermonChat
                  key={focused.key}
                  user={user}
                  videoIds={focused.videoIds}
                  chatId={focused.chatId}
                  onBack={() => setCurrentView('focused')}
                  onRequestSignIn={handleRequestSignIn}
                  onDirtyChange={(d) => setDirty('focused', d)}
                  onChatSaved={handleChatSaved}
                  onAnswerComplete={() => handleAnswerComplete('focused')}
                />
              ) : (
                <ChatInterface
                  key={focused.key}
                  user={user}
                  videoIds={focused.videoIds}
                  chatId={focused.chatId}
                  onBack={() => setCurrentView('focused')}
                  onRequestSignIn={handleRequestSignIn}
                  onDirtyChange={(d) => setDirty('focused', d)}
                  onChatSaved={handleChatSaved}
                  onAnswerComplete={() => handleAnswerComplete('focused')}
                />
              )}
            </Suspense>
          </div>
        )}
      </AppShell>

      {showAuthForm && (
        <div className="auth-overlay">
          <button
            type="button"
            className="auth-overlay-close"
            onClick={() => setShowAuthForm(false)}
            aria-label="Back to chat"
          >
            <Icon name="x" size={24} />
          </button>
          <AuthForm
            onContinueAsGuest={() => setShowAuthForm(false)}
            onSignedIn={() => {
              setUser({ ...auth.currentUser });
              setMonitoringUser(auth.currentUser);
              setShowAuthForm(false);
            }}
          />
        </div>
      )}

      {showAccount && !user.isAnonymous && (
        <Suspense fallback={null}>
          <AccountMenu
            user={user}
            onClose={() => setShowAccount(false)}
            onUpdated={(updated) => setUser({ ...updated })}
            onDeleted={handleAccountDeleted}
            onContact={() => { setShowAccount(false); openContact(); }}
            onSignOut={handleSignOut}
          />
        </Suspense>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete this conversation?"
          body={<p>“{(confirmDelete.title || 'This conversation').slice(0, 80)}” will be permanently removed from your library. This can't be undone.</p>}
          confirmLabel="Delete"
          danger
          busy={deleting}
          onConfirm={performDelete}
          onCancel={() => { if (!deleting) setConfirmDelete(null); }}
        />
      )}

      {pendingNav && (
        <UnsavedModal
          isGuest={user.isAnonymous}
          skipChecked={skipChecked}
          onSkipChange={setSkipChecked}
          onStay={() => setPendingNav(null)}
          onDiscard={confirmDiscard}
          onRequestSignIn={() => { setPendingNav(null); handleRequestSignIn(); }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </TeamRoleContext.Provider>
  );
}

export default App;
